<template>
  <div class="flex flex-col w-full h-full">
    <div class="flex w-full h-full justify-center items-center px-4" ref="viewer">
      <div class="flex flex-col w-full h-full items-center">
        <date-picker :records="records" :selectIndex="leftViewSelectedIndex" :subTitle="$t('homePage.leftCompareView')"
          @select="onLeftSelect" />
        <model-viewer v-show="show3D && leftObjUrl && rightObjUrl" bg-white :shader-type="leftShaderType" class="w-full h-full"
          :obj-url="leftObjUrl" :zoom-factor="1.25" :fov="55" :measurement="leftViewActiveMeasurement" :controlDomElement="$refs.viewer"
          :disableEnterAnimation="true" />
      </div>

      <div class="flex flex-col w-full h-full items-center">
        <date-picker :records="records" :selectIndex="rightViewSelectedIndex" :subTitle="$t('homePage.rightCompareView')"
          @select="onRightSelect" />
        <model-viewer v-show="show3D && rightObjUrl && leftObjUrl" bg-white :shader-type="shaderType" class="w-full h-full"
          :obj-url="rightObjUrl" :zoom-factor="1.25" :fov="55" :measurement="rightViewActiveMeasurement" :controlDomElement="$refs.viewer"
          :disableEnterAnimation="true" />
      </div>
    </div>

    <div class="split-line"></div>
    <div class="pl-8 flex mt-4 mb-4">
      <span>{{ $t('homePage.measurement') }}</span>
      <unit-switcher v-if="!measurementFailed" class="ml-auto mr-6" :use-inch="useInch" :layout="'white'"
        @toggle="onUnitSwitch" />
    </div>
    <measurement-page :countPerPage="5" :measurements="compareMeasurements" :active-measurement="activeMeasurement"
      :use-inch="useInch" :is-compare="true" @click="onMeasurementClick" />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import CacheManager from '@/modules/cache-manager';
import measurements from '@/mixins/measurements';
import UnitSwitcher from '@/components/common/UnitSwitcher.vue';
import DatePicker from '@/components/common/DatePicker.vue';
import ModelViewer from '@/components/common/ModelViewer.vue';
import MeasurementPage from './MeasurementPage.vue';

export default {
  name: 'ComparePage',
  mixins: [measurements],
  components: {
    UnitSwitcher,
    DatePicker,
    ModelViewer,
    MeasurementPage,
  },
  props: {
    records: Array,
    tid: String,
    show3D: Boolean,
  },
  data() {
    return {
      activeMeasurement: null,
      rightViewSelectedIndex: 0,
      leftViewSelectedIndex: 0,
      leftObjUrl: '',
      rightObjUrl: '',
      leftViewMeasurements: [],
      rightViewMeasurements: [],
    };
  },
  mounted() {
    this.rightViewSelectedIndex = this.records.findIndex(r => r.tid === this.tid);
    this.leftViewSelectedIndex = Math.min(this.rightViewSelectedIndex + 1, this.records.length - 1);
  },
  computed: {
    ...mapState('app', ['scannersConfigs']),
    scannerId() {
      const record = this.records[this.rightViewSelectedIndex];
      const { uuid = '' } = record.scanner || {};
      return uuid;
    },
    shaderType() {
      let type = 'default';
      if (this.scannerId && this.scannersConfigs[this.scannerId]) {
        type = this.scannersConfigs[this.scannerId].shader_type || 'default';
      }

      return type;
    },
    leftScannerId() {
      const record = this.records[this.leftViewSelectedIndex];
      const { uuid = '' } = record.scanner || {};
      return uuid;
    },
    leftShaderType() {
      let type = 'default';
      if (this.leftScannerId && this.scannersConfigs[this.leftScannerId]) {
        type = this.scannersConfigs[this.leftScannerId].shader_type || 'default';
      }

      return type;
    },
    customMeasurements() {
      if (this.scannerId && this.scannersConfigs[this.scannerId]) {
        return this.scannersConfigs[this.scannerId].custom_measurements;
      }
      return null;
    },
    leftViewRecord() {
      return this.records[this.leftViewSelectedIndex];
    },
    rightViewRecord() {
      return this.records[this.rightViewSelectedIndex];
    },
    compareMeasurements() {
      const ret = [];
      this.rightViewMeasurements.forEach(m => {
        const mes2 = this.leftViewMeasurements.find(m2 => m2.key === m.key);

        ret.push({
          ...m,
          rightMes: null, // split left and right to two measurements, so set the right one to null
          another: { ...mes2, rightMes: null },
          isLeft: m.rightMes !== null ? true : null,
        });

        if (m.rightMes) {
          ret.push({
            ...m.rightMes,
            another: mes2.rightMes,
            isRight: true,
          });
        }
      });
      return ret;
    },
    leftViewActiveMeasurement() {
      return this.activeMeasurement ? this.activeMeasurement.another : null;
    },
    rightViewActiveMeasurement() {
      return this.activeMeasurement;
    },
  },
  watch: {
    show3D(nv) {
      if (nv) {
        if (!this.leftObjUrl || !this.rightObjUrl) {
          this.loadOBJAndMeasurements();
        }
      }
    }
  },
  methods: {
    ...mapActions('user', ['getSizeXt']),
    async getMeasurements(tid) {
      const data = await this.getSizeXt({ tid });
      return this.processMeasurements(data, true, true, this.customMeasurements);
    },
    onMeasurementClick(m) {
      this.activeMeasurement = (m === this.activeMeasurement) ? null : m;
    },
    async loadOBJAndMeasurements() {
      this.$events.startLoading();
      this.onMeasurementClick(null);

      try {
        const [leftTid, rightTid] = [this.leftViewRecord.tid, this.rightViewRecord.tid];
        [this.leftViewMeasurements, this.rightViewMeasurements] = await Promise.all([
          this.getMeasurements(leftTid),
          this.getMeasurements(rightTid),
        ]);

        [this.leftObjUrl, this.rightObjUrl] = await Promise.all([
          CacheManager.loadTgs(leftTid),
          CacheManager.loadTgs(rightTid),
        ]);
      } catch (error) {
        this.$events.alert(this.$t('welcome.unexpected_error'));
      } finally {
        this.$events.stopLoading();
      }
    },
    async onLeftSelect(index) {
      this.leftViewSelectedIndex = index;
      this.leftObjUrl = '';
      this.loadOBJAndMeasurements();
    },
    async onRightSelect(index) {
      this.rightViewSelectedIndex = index;
      this.rightObjUrl = '';
      this.loadOBJAndMeasurements();
    },
  },
};
</script>

<style lang="scss" scoped>
.dropdown {
  background: rgba(191, 191, 191, 0.15);
  border-radius: 4px;
  width: 158px;
  height: 44px;
}

.split-line {
  width: 100%;
  border: 1px solid #D9D9D9;
}
</style>
